
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import moment from 'moment';
import {getTableName} from '@/worker/db/sqlHelpers';

@Component({
	components: {
		TableFilterable,
		SelectFilter,
	},
})
export default class CustomerOverdueTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['customers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'duePeriod'
	> = ['customers', 'agents', 'dateAsOf', 'duePeriod'];

	public period: '14 days' | '30 days' | '60 days' = '30 days';
	public periodOptions = ['14 days', '30 days', '60 days'];

	public tableItems: any[] = [];
	public tableFields: any[] = [];

	public get exportFileName() {
		const formattedDate = moment(this.selectedAsOfDate).format('DD MMM YY');
		return 'Customer Overdue' + '_' + 'As Of ' + formattedDate;
	}

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get selectedDate() {
		const periodArgument = this.period.split(' ');
		const day = Number(periodArgument[0]);
		return day;
	}

	public get expensiveHook() {
		const {
			selectedDate,
			selectedCustomers,
			selectedAsOfDate,
			selectedAgents,
		} = this;
		return JSON.stringify([
			selectedDate,
			selectedCustomers,
			selectedAsOfDate,
			selectedAgents,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const table = getTableName('customerIvDns');

		let customerFilter = '';
		if (this.selectedCustomers.length > 0) {
			const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['customerIvDns']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		const rows = await queryHead.rawQuery(`
			SELECT "DOCTYPE" as doc_type, "DOCNO" as doc_no, "CODE" as code,
				TRY_CAST("DOCDATE" AS BIGINT) as doc_date,
				"TERMS" as terms,
				TRY_CAST("DUEDATE" AS BIGINT) as due_date,
				"AGENT" as agent,
				TRY_CAST("LOCALDOCAMT" AS DOUBLE) as amount,
				TRY_CAST("LOCALDOCOSAMT" AS DOUBLE) as os_amount
			FROM ${table}
			WHERE TRY_CAST("DUEDATE" AS BIGINT) <= ${this.selectedAsOfDate}
				AND TRY_CAST("LOCALDOCOSAMT" AS DOUBLE) != 0
				${customerFilter}${agentFilter}
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9);

		this.tableFields = [
			{
				key: 'age',
				sortable: false,
				formatter: TableItemFormatter.basicInteger,
			},
			{
				key: 'doc_date',
				sortable: true,
				formatter: TableItemFormatter.date,
			},
			{
				key: 'due_date',
				sortable: true,
				formatter: TableItemFormatter.date,
			},
			{
				key: 'term',
				sortable: false,
			},
			{
				key: 'customer',
				sortable: true,
			},
			{
				key: 'agent',
				sortable: true,
			},
			{
				key: 'doc_no',
				sortable: false,
			},
			{
				key: 'ori._amount',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
			{
				key: 'outstanding_amount',
				sortable: true,
				formatter: TableItemFormatter.currencySplit,
			},
		];

		this.tableItems = rows.map((row) => ({
			'age': Math.ceil(moment.duration(this.selectedAsOfDate - (Number(row.due_date) || 0)).asDays()),
			'doc_date': Number(row.doc_date) || 0,
			'due_date': Number(row.due_date) || 0,
			'doc_no': row.doc_no,
			'term': row.terms,
			'customer': gdbx.customerNames[row.code],
			'agent': gdbx.agentNames[row.agent],
			'ori._amount': Number(row.amount) || 0,
			'outstanding_amount': Number(row.os_amount) || 0,
		}));

		this.generateLoadingText(1);
		this.saveHistory('tableItems', 'tableFields');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
